import { HStack, Text, VStack } from '@ayble-health/dashboard-components';
import { Box, useToken } from '@chakra-ui/react';
import { Check } from 'lucide-react';
import { formatDate } from '../utils';
import { getRequestedNextStepLabel } from './utils';
import { useSuspectIdentification } from 'api/healthCoach/useSuspectIdentification';
import { useUserBlockFoods } from 'api/healthCoach/useUserBlockFoods';
import { Loader } from 'components/Loader';
import { IdentifiedTriggersFoods } from './IdentifiedTriggersFoods';

type Props = {
  blockId?: string;
};

export const SuspectIdentificationDetailsContent = ({ blockId }: Props) => {
  const { foods, isLoading: isFoodsLoading } = useUserBlockFoods({
    userBlockId: blockId,
  });
  const { suspectIdentification, isLoading: isCheckInLoading } =
    useSuspectIdentification({
      suspectIdentificationOrUserBlockId: blockId,
    });
  const requestedFoods = suspectIdentification?.desired_food_list || [];
  const nextStepLabel = getRequestedNextStepLabel(
    suspectIdentification?.connect_preference || null
  );
  const formattedEndedOn = formatDate(
    suspectIdentification?.ended_on ?? undefined,
    'MMM D'
  );
  const [secondary200] = useToken('colors', ['secondary.200']);

  const renderTriggersSection = () => {
    if (isFoodsLoading) {
      return <Loader loading />;
    }

    if (foods.length > 0) {
      return <IdentifiedTriggersFoods foods={foods} />;
    }

    return null;
  };

  const renderAppCheckInSection = () => {
    if (isCheckInLoading) {
      return <Loader loading />;
    }

    if (!suspectIdentification) {
      return (
        <Box
          bgColor={'feather.100'}
          borderColor={'cloud.200'}
          borderRadius={'xl'}
          borderWidth={1}
          overflow={'hidden'}
        >
          <HStack
            alignItems={'center'}
            borderBottomWidth={1}
            borderColor={'cloud.200'}
            justifyContent={'space-between'}
            px={4}
            py={3}
          >
            <Text color={'secondary.500'} fontSize={'md'} fontWeight={'medium'}>
              App Check-in
            </Text>
          </HStack>

          <Box px={4} py={4}>
            {isFoodsLoading ? (
              <Loader loading />
            ) : (
              <Text color={'blueGray.600'} fontSize={'sm'}>
                {foods.length > 0
                  ? 'Suspects have been identified, but member has not submitted feedback yet.'
                  : 'Member is still logging gut checks, and has not yet been prompted for feedback.'}
              </Text>
            )}
          </Box>
        </Box>
      );
    }

    return (
      <Box
        bgColor={'feather.100'}
        borderColor={'cloud.200'}
        borderRadius={'xl'}
        borderWidth={1}
        overflow={'hidden'}
      >
        <HStack
          alignItems={'center'}
          borderBottomWidth={1}
          borderColor={'cloud.200'}
          justifyContent={'space-between'}
          px={4}
          py={3}
        >
          <Text color={'secondary.500'} fontSize={'md'} fontWeight={'medium'}>
            App Check-in
          </Text>
          <Text color={'secondary.200'} fontSize={'sm'} fontWeight={'medium'}>
            {formattedEndedOn}
          </Text>
        </HStack>

        <VStack px={4} py={4} gap={4}>
          <HStack alignItems={'flex-start'} gap={2}>
            <Check color={secondary200} size={12} style={{ marginTop: 4 }} />

            <Text
              color={'secondary.500'}
              flex={1}
              fontSize={'md'}
              fontWeight={'normal'}
            >
              Requested next steps:{' '}
              <Text color={'secondary.500'} fontSize={'md'} fontWeight={'bold'}>
                {nextStepLabel}
              </Text>
            </Text>
          </HStack>

          <HStack alignItems={'flex-start'} gap={2}>
            <Check color={secondary200} size={12} style={{ marginTop: 4 }} />

            <VStack flex={1} gap={0.5}>
              <Text
                color={'secondary.500'}
                fontSize={'md'}
                fontWeight={'normal'}
              >
                Requested suspect trigger list:
              </Text>

              {!!requestedFoods.length &&
                requestedFoods.map((food) => (
                  <Text
                    key={food.id}
                    color={'secondary.500'}
                    fontSize={'md'}
                    fontWeight={'bold'}
                    pl={2}
                  >
                    - {food.display_name}
                  </Text>
                ))}

              {!requestedFoods.length && (
                <Text
                  color={'blueGray.600'}
                  fontSize={'sm'}
                  fontWeight={'bold'}
                  pl={2}
                >
                  --
                </Text>
              )}
            </VStack>
          </HStack>

          <Text color={'secondary.500'} fontSize={'md'} fontWeight={'normal'}>
            {suspectIdentification.open_feedback
              ? `"${suspectIdentification.open_feedback}"`
              : '--'}
          </Text>
        </VStack>
      </Box>
    );
  };

  return (
    <VStack alignItems={'stretch'} gap={4}>
      {renderTriggersSection()}
      {renderAppCheckInSection()}
    </VStack>
  );
};
